import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type SupabaseAnalysisRow = Database['public']['Tables']['seo_analyses']['Row'];
type SupabaseAnalysisInsert = Database['public']['Tables']['seo_analyses']['Insert'];
type SupabaseAnalysisUpdate = Database['public']['Tables']['seo_analyses']['Update'];

export interface SupabaseAnalysis {
  id?: string;
  report_id: string;
  lead_email?: string;
  request_datetime: string;
  response_datetime?: string;
  total_analysis_time_seconds?: number;
  url_analyzed: string;
  keyword_analyzed: string;
  seo_score?: number;
  analysis_status: 'processing' | 'completed' | 'error';
  error_message?: string;
  created_at?: string;
  updated_at?: string;
}

class SupabaseAnalysisService {
  async startAnalysis(
    reportId: string,
    url: string,
    keyword: string,
    leadEmail?: string
  ): Promise<string | null> {
    try {
      console.log('🔄 Iniciando registro de análise no Supabase:', { reportId, url, keyword, leadEmail });
      
      const analysisData: SupabaseAnalysisInsert = {
        report_id: reportId,
        lead_email: leadEmail,
        request_datetime: new Date().toISOString(),
        url_analyzed: url,
        keyword_analyzed: keyword,
        analysis_status: 'processing'
      };

      console.log('📝 Dados para inserção:', analysisData);

      const { data, error } = await supabase
        .from('seo_analyses')
        .insert(analysisData)
        .select()
        .single();

      if (error) {
        console.error('❌ Erro ao iniciar registro de análise:', error);
        console.error('❌ Detalhes do erro:', JSON.stringify(error, null, 2));
        return null;
      }

      console.log('✅ Análise registrada no Supabase com sucesso:', data);
      return data.id;
    } catch (error) {
      console.error('❌ Erro ao processar início de análise:', error);
      console.error('❌ Stack trace:', error instanceof Error ? error.stack : 'N/A');
      return null;
    }
  }

  async completeAnalysis(
    reportId: string,
    seoScore?: number,
    totalTimeSeconds?: number
  ): Promise<boolean> {
    try {
      console.log('🔄 Completando análise no Supabase:', { reportId, seoScore, totalTimeSeconds });
      
      // Primeiro, vamos verificar se o registro existe
      const { data: existingRecord, error: selectError } = await supabase
        .from('seo_analyses')
        .select('*')
        .eq('report_id', reportId)
        .single();

      if (selectError && selectError.code !== 'PGRST116') {
        console.error('❌ Erro ao buscar registro existente:', selectError);
      } else if (selectError && selectError.code === 'PGRST116') {
        console.log('⚠️ Registro não encontrado para report_id:', reportId);
        console.log('⚠️ Tentando criar novo registro...');
        
        // Se não encontrou, vamos criar um novo registro
        const analysisData: SupabaseAnalysisInsert = {
          report_id: reportId,
          request_datetime: new Date().toISOString(),
          response_datetime: new Date().toISOString(),
          url_analyzed: 'unknown',
          keyword_analyzed: 'unknown',
          analysis_status: 'completed',
          seo_score: seoScore,
          total_analysis_time_seconds: totalTimeSeconds
        };

        const { data: newData, error: insertError } = await supabase
          .from('seo_analyses')
          .insert(analysisData)
          .select()
          .single();

        if (insertError) {
          console.error('❌ Erro ao criar novo registro:', insertError);
          return false;
        }

        console.log('✅ Novo registro criado:', newData);
        return true;
      } else {
        console.log('📋 Registro existente encontrado:', existingRecord);
      }
      
      const updateData: SupabaseAnalysisUpdate = {
        response_datetime: new Date().toISOString(),
        analysis_status: 'completed',
        seo_score: seoScore,
        total_analysis_time_seconds: totalTimeSeconds
      };

      console.log('📝 Dados para atualização:', updateData);

      const { data: updateResult, error } = await supabase
        .from('seo_analyses')
        .update(updateData)
        .eq('report_id', reportId)
        .select()
        .single();

      if (error) {
        console.error('❌ Erro ao completar análise:', error);
        console.error('❌ Detalhes do erro:', JSON.stringify(error, null, 2));
        return false;
      }

      console.log('✅ Análise completada no Supabase com sucesso:', updateResult);
      return true;
    } catch (error) {
      console.error('❌ Erro ao processar conclusão de análise:', error);
      console.error('❌ Stack trace:', error instanceof Error ? error.stack : 'N/A');
      return false;
    }
  }

  async markAnalysisAsError(
    reportId: string,
    errorMessage: string
  ): Promise<boolean> {
    try {
      console.log('🔄 Marcando análise como erro no Supabase:', { reportId, errorMessage });
      
      const updateData: SupabaseAnalysisUpdate = {
        response_datetime: new Date().toISOString(),
        analysis_status: 'error',
        error_message: errorMessage
      };

      const { error } = await supabase
        .from('seo_analyses')
        .update(updateData)
        .eq('report_id', reportId);

      if (error) {
        console.error('❌ Erro ao marcar análise como erro:', error);
        return false;
      }

      console.log('✅ Análise marcada como erro no Supabase:', reportId);
      return true;
    } catch (error) {
      console.error('❌ Erro ao processar erro de análise:', error);
      return false;
    }
  }

  async getAnalysisByReportId(reportId: string): Promise<SupabaseAnalysis | null> {
    try {
      console.log('🔍 Buscando análise por report_id:', reportId);
      
      const { data, error } = await supabase
        .from('seo_analyses')
        .select('*')
        .eq('report_id', reportId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          console.log('ℹ️ Análise não encontrada para report_id:', reportId);
          return null;
        }
        console.error('❌ Erro ao buscar análise:', error);
        return null;
      }

      console.log('✅ Análise encontrada:', data);
      return data as SupabaseAnalysis;
    } catch (error) {
      console.error('❌ Erro ao buscar análise:', error);
      return null;
    }
  }

  async getAnalysesByEmail(email: string): Promise<SupabaseAnalysis[]> {
    try {
      const { data, error } = await supabase
        .from('seo_analyses')
        .select('*')
        .eq('lead_email', email)
        .order('request_datetime', { ascending: false });

      if (error) {
        console.error('Erro ao buscar análises por email:', error);
        return [];
      }

      return (data || []) as SupabaseAnalysis[];
    } catch (error) {
      console.error('Erro ao buscar análises por email:', error);
      return [];
    }
  }
}

export const supabaseAnalysisService = new SupabaseAnalysisService();
